import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import AgentLayout from "../components/layout/AgentLayout";
import PageHeader from "../components/common/PageHeader";
import { DASHBOARD_CRUMB } from "../components/common/Breadcrumb";
import AgentSelect from "../components/common/AgentSelect";
import PageLoader from "../components/common/PageLoader";
import StatusBadge from "../components/common/StatusBadge";
import Table from "../components/common/Table";
import { agentApi } from "../lib/apiClient";
import { showApiError, showErrorAction, showSuccessAction, notifyFinanceUpdated } from "../lib/alerts";
import { asArray, formatCurrency, unwrapData } from "../lib/catalog";
import { canUploadPaymentProof, formatPaymentStatus, isDraftBooking, isPaidPayment } from "../lib/bookingStatus";

const EMPTY_FORM = {
  bookingId: "",
  amount: "",
  paymentType: "full",
  paymentMethod: "bank_transfer",
  referenceNumber: "",
  remarks: "",
  file: null,
};

function proofAmountFromSummary(summary) {
  const outstanding = Number(summary?.outstanding);
  if (!Number.isFinite(outstanding) || outstanding <= 0) {
    return "";
  }
  return String(outstanding);
}

function summaryBookingTotal(summary) {
  return summary?.bookingTotal ?? summary?.booking_total ?? summary?.total ?? 0;
}

function toMoney(value) {
  const amount = Number(String(value || "").replace(/,/g, ""));
  return Number.isFinite(amount) ? amount : 0;
}

function positiveCount(value) {
  const count = Number.parseInt(value, 10);
  return Number.isFinite(count) && count > 0 ? count : 0;
}

function summaryBreakdown(summary) {
  return summary?.breakdown || summary?.summary?.breakdown || {};
}

function packageAmountSummary(summary, selectedBooking = null) {
  const breakdown = summaryBreakdown(summary);
  const total = toMoney(summaryBookingTotal(summary) || selectedBooking?.total_amount);
  const paid = toMoney(summary?.successfulPayments ?? summary?.successful_payments);
  const refunded = toMoney(summary?.refundedAmount ?? summary?.refunded_amount);
  const outstanding = toMoney(summary?.outstanding ?? Math.max(total - paid + refunded, 0));
  const pilgrims = positiveCount(breakdown.pilgrims)
    || positiveCount(summary?.booking?.upbu_no_of_passengers)
    || positiveCount(summary?.booking?.passenger_limit)
    || positiveCount(selectedBooking?.passenger_limit)
    || positiveCount(selectedBooking?.upbu_no_of_passengers);
  const packageName = breakdown.package_name
    || summary?.booking?.package_name
    || summary?.booking?.upbu_pkg_name
    || selectedBooking?.package_name
    || "";
  const apiLines = asArray(breakdown.lines).filter((row) => toMoney(row.amount) > 0 || positiveCount(row.count) > 0);
  const unitRate = toMoney(breakdown.unit_rate) || (pilgrims > 0 ? total / pilgrims : total);
  const lines = apiLines.length > 0
    ? apiLines
    : (total > 0 && pilgrims > 0
      ? [{
          label: `${pilgrims} pilgrim${pilgrims === 1 ? "" : "s"} × package rate`,
          count: pilgrims,
          rate: unitRate,
          amount: total,
        }]
      : (total > 0
        ? [{ label: "Package booking total", count: 1, rate: total, amount: total }]
        : []));

  return {
    packageName,
    pilgrims,
    lines,
    total,
    paid,
    refunded,
    outstanding,
  };
}

export default function PaymentProofPage() {
  const [searchParams] = useSearchParams();
  const presetBookingId = String(searchParams.get("bookingId") || "").trim();
  const presetMethod = String(searchParams.get("method") || "").trim().toLowerCase();
  const [bookings, setBookings] = useState([]);
  const [methods, setMethods] = useState([]);
  const [payments, setPayments] = useState([]);
  const [account, setAccount] = useState(null);
  const [form, setForm] = useState({
    ...EMPTY_FORM,
    bookingId: presetBookingId,
    paymentMethod: presetMethod === "wallet" ? "wallet" : EMPTY_FORM.paymentMethod,
  });
  const [bookingSummary, setBookingSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const selectedBooking = bookings.find((booking) => String(booking.booking_id) === String(form.bookingId)) || null;
  const amountSummary = packageAmountSummary(bookingSummary, selectedBooking);
  const walletBalance = Number(account?.wallet_balance || 0);
  const payingFromWallet = String(form.paymentMethod || "").toLowerCase() === "wallet";
  const selectedPaymentStatus = bookingSummary?.paymentStatus || bookingSummary?.payment_status;
  const paymentClosed = !canUploadPaymentProof(
    selectedBooking?.booking_status || bookingSummary?.booking?.booking_status,
    selectedPaymentStatus
  );
  const paymentAlreadyPaid = isPaidPayment(selectedPaymentStatus);
  const recentForBooking = form.bookingId
    ? payments.filter((row) => String(row.booking_id) === String(form.bookingId))
    : payments;

  async function loadPayments() {
    const payload = await agentApi.get("/payments/me", { params: { limit: 25, page: 1 } });
    const data = unwrapData(payload);
    setPayments(asArray(data));
  }

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [bookingPayload, methodPayload, accountPayload] = await Promise.all([
          agentApi.get("/travel-partner/bookings", { params: { limit: 100, page: 1 } }),
          agentApi.get("/payments/methods"),
          agentApi.get("/travel-partner/finance/account").catch(() => null),
        ]);
        if (!alive) return;
        setBookings(asArray(unwrapData(bookingPayload)).filter((booking) => !isDraftBooking(booking.booking_status)));
        const methodData = unwrapData(methodPayload);
        const loadedMethods = Array.isArray(methodData) ? methodData : asArray(methodData);
        if (!loadedMethods.some((method) => String(method.code || method).toLowerCase() === "wallet")) {
          loadedMethods.unshift({ code: "wallet", label: "Wallet" });
        }
        setMethods(loadedMethods);
        const accountData = unwrapData(accountPayload);
        setAccount(accountData && typeof accountData === "object" ? accountData : null);
        await loadPayments();
      } catch (fetchError) {
        if (alive) showApiError(fetchError, "LOAD_PAYMENTS");
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;
    async function loadSummary() {
      if (!form.bookingId) {
        setBookingSummary(null);
        return;
      }
      try {
        const payload = await agentApi.get(`/payments/booking/${form.bookingId}/summary`);
        if (!alive) return;
        const data = unwrapData(payload);
        const summary = data?.summary || data || null;
        setBookingSummary(summary);
        const nextAmount = proofAmountFromSummary(summary);
        setForm((current) => {
          if (current.bookingId !== form.bookingId) return current;
          const paymentType = current.paymentType || "full";
          if (paymentType === "partial" && current.amount) return current;
          return { ...current, paymentType, amount: nextAmount };
        });
      } catch (_error) {
        if (alive) setBookingSummary(null);
      }
    }
    loadSummary();
    return () => {
      alive = false;
    };
  }, [form.bookingId]);

  return (
    <AgentLayout>
      <div className="container-fluid py-24">
        <PageHeader
          eyebrow="Payment proof"
          title="Submit a collection proof against a booking"
          description="Pay from wallet to apply the amount immediately. Bank, UPI, cash, and other methods still upload a proof and stay pending until an administrator marks them received."
          items={[DASHBOARD_CRUMB, { label: "Payment Proofs" }]}
          backTo="/dashboard"
          actions={
            <Link to="/wallet-credit" className="btn btn-outline-primary btn-sm">Open outstanding</Link>
          }
        />

        {loading ? <PageLoader message="Loading payment proof form..." /> : null}

        {!loading ? (
          <div className="row g-4">
            <div className="col-12 col-lg-5">
              <div className="card border-0 shadow-sm h-100">
                <div className="card-body p-24">
                  <h5 className="mb-3">Upload proof</h5>
                  <div className="row g-3">
                    <div className="col-12">
                      <label className="form-label">Booking</label>
                      <AgentSelect
                        value={form.bookingId}
                        options={bookings.map((booking) => ({
                          value: String(booking.booking_id),
                          label: `${booking.booking_reference} — ${booking.customer_name || "Unassigned"}`,
                        }))}
                        onChange={(value) => setForm({ ...form, bookingId: value || "", amount: "", paymentType: "full" })}
                        placeholder="Select booking"
                        isClearable
                      />
                    </div>
                    {bookingSummary ? (
                      <div className="col-12">
                        <div className="small bg-neutral-100 rounded-3 p-3">
                          <div className="d-flex flex-wrap justify-content-between gap-2">
                            <span>Booking total: {formatCurrency(summaryBookingTotal(bookingSummary))}</span>
                            <span>Outstanding: {formatCurrency(bookingSummary.outstanding)}</span>
                            <StatusBadge value={bookingSummary.paymentStatus || bookingSummary.payment_status || "pending"}>
                              {formatPaymentStatus(bookingSummary.paymentStatus || bookingSummary.payment_status)}
                            </StatusBadge>
                          </div>
                          <div className="text-secondary mt-1">
                            Paid {formatCurrency(bookingSummary.successfulPayments ?? bookingSummary.successful_payments)}
                            {toMoney(bookingSummary.refundedAmount ?? bookingSummary.refunded_amount) > 0
                              ? ` · Refunded ${formatCurrency(bookingSummary.refundedAmount ?? bookingSummary.refunded_amount)}`
                              : ""}
                            {paymentAlreadyPaid
                              ? ". This booking is already paid, so a new payment proof is not needed."
                              : paymentClosed
                              ? ". New payment proofs are not accepted on a cancelled or refunded booking."
                              : ". Choose Full to pay the outstanding package total, or Partial to store a smaller collection."}
                          </div>
                        </div>
                      </div>
                    ) : null}
                    {paymentClosed ? (
                      <div className="col-12">
                        <div className="alert alert-warning mb-0">
                          {paymentAlreadyPaid
                            ? "This booking is already paid. Upload proof is closed."
                            : "This booking is cancelled or refunded. Submit proof is closed."}
                        </div>
                      </div>
                    ) : null}
                    <div className="col-12 col-md-6">
                      <label className="form-label">Payment type</label>
                      <AgentSelect
                        value={form.paymentType}
                        options={[
                          { value: "full", label: "Full outstanding" },
                          { value: "partial", label: "Partial payment" },
                        ]}
                        onChange={(value) => {
                          const paymentType = value || "full";
                          const outstanding = proofAmountFromSummary(bookingSummary);
                          setForm({
                            ...form,
                            paymentType,
                            amount: paymentType === "full" ? outstanding : "",
                          });
                        }}
                        isDisabled={paymentClosed}
                        isSearchable={false}
                        placeholder="Select type"
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Amount</label>
                      <input
                        className="form-control"
                        value={form.amount}
                        disabled={paymentClosed}
                        readOnly={form.paymentType === "full"}
                        onChange={(event) => setForm({ ...form, amount: event.target.value })}
                      />
                    </div>
                    <div className="col-12 col-md-6">
                      <label className="form-label">Method</label>
                      <AgentSelect
                        value={form.paymentMethod}
                        options={(methods.length > 0 ? methods : [{ code: "bank_transfer", label: "Bank Transfer" }]).map((method) => {
                          const code = method.code || method;
                          const isWallet = String(code).toLowerCase() === "wallet";
                          return {
                            value: code,
                            label: isWallet
                              ? `Wallet — ${formatCurrency(walletBalance)} available`
                              : (method.label || method.code || method),
                          };
                        })}
                        onChange={(value) => setForm({ ...form, paymentMethod: value || "bank_transfer" })}
                        isDisabled={paymentClosed}
                        isSearchable={false}
                        placeholder="Select method"
                      />
                    </div>
                    {payingFromWallet ? (
                      <div className="col-12">
                        <div className="small bg-success-subtle rounded-3 p-3">
                          Wallet pays the booking immediately. No proof file is needed.
                          Credit limit is separate: unpaid bookings already sit against it until this outstanding is cleared.
                        </div>
                      </div>
                    ) : null}
                    {payingFromWallet ? null : (
                    <div className="col-12">
                      <label className="form-label">Reference number</label>
                      <input className="form-control" value={form.referenceNumber} disabled={paymentClosed} onChange={(event) => setForm({ ...form, referenceNumber: event.target.value })} />
                    </div>
                    )}
                    <div className="col-12">
                      <label className="form-label">Remarks</label>
                      <textarea className="form-control" rows={2} value={form.remarks} disabled={paymentClosed} onChange={(event) => setForm({ ...form, remarks: event.target.value })} />
                    </div>
                    {payingFromWallet ? null : (
                    <div className="col-12">
                      <label className="form-label">Proof file</label>
                      <input type="file" className="form-control" disabled={paymentClosed} onChange={(event) => setForm({ ...form, file: event.target.files?.[0] || null })} />
                    </div>
                    )}
                    <div className="col-12">
                      <button
                        type="button"
                        className="btn btn-primary"
                        disabled={saving || paymentClosed}
                        onClick={async () => {
                          try {
                            setSaving(true);
                            if (paymentClosed) {
                              showErrorAction(paymentAlreadyPaid ? "PAYMENT_ALREADY_PAID" : "PAYMENT_CLOSED_BOOKING");
                              return;
                            }
                            if (!form.bookingId) {
                              showErrorAction(payingFromWallet ? "WALLET_PAYMENT_REQUIRED" : "PAYMENT_PROOF_REQUIRED");
                              return;
                            }
                            if (!payingFromWallet && !form.file) {
                              showErrorAction("PAYMENT_PROOF_REQUIRED");
                              return;
                            }
                            const outstanding = toMoney(bookingSummary?.outstanding);
                            const amount = form.paymentType === "full"
                              ? outstanding || toMoney(form.amount)
                              : toMoney(form.amount);
                            if (amount <= 0) {
                              showErrorAction(payingFromWallet ? "WALLET_PAYMENT_REQUIRED" : "PAYMENT_PROOF_REQUIRED");
                              return;
                            }
                            if (payingFromWallet && amount > walletBalance) {
                              showErrorAction("WALLET_BALANCE_LOW");
                              return;
                            }
                            if (form.paymentType === "partial" && (outstanding <= 0 || amount >= outstanding)) {
                              showErrorAction("PAYMENT_PARTIAL_AMOUNT");
                              return;
                            }
                            const remarks = form.paymentType === "partial"
                              ? [form.remarks, "Partial payment"].filter(Boolean).join(" — ")
                              : form.remarks;
                            if (payingFromWallet) {
                              await agentApi.post(`/payments/booking/${form.bookingId}/wallet`, {
                                body: {
                                  amount,
                                  remarks,
                                },
                              });
                              setForm(EMPTY_FORM);
                              setBookingSummary(null);
                              showSuccessAction("PAY_FROM_WALLET");
                              notifyFinanceUpdated();
                            } else {
                              const body = new FormData();
                              body.append("proof", form.file);
                              body.append("amount", String(amount));
                              body.append("paymentMethod", form.paymentMethod);
                              body.append("referenceNumber", form.referenceNumber);
                              body.append("remarks", remarks);
                              await agentApi.post(`/payments/booking/${form.bookingId}/proof`, { body });
                              setForm(EMPTY_FORM);
                              setBookingSummary(null);
                              showSuccessAction("SUBMIT_PAYMENT");
                            }
                            const [accountPayload] = await Promise.all([
                              agentApi.get("/travel-partner/finance/account").catch(() => null),
                              loadPayments(),
                            ]);
                            const accountData = unwrapData(accountPayload);
                            setAccount(accountData && typeof accountData === "object" ? accountData : account);
                          } catch (saveError) {
                            showApiError(saveError, payingFromWallet ? "PAY_FROM_WALLET" : "SUBMIT_PAYMENT");
                          } finally {
                            setSaving(false);
                          }
                        }}
                      >
                        {saving ? "Submitting..." : payingFromWallet ? "Pay from wallet" : "Submit proof"}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="col-12 col-lg-7">
              <div className="card border-0 shadow-sm mb-4">
                <div className="card-body p-24">
                  <h5 className="mb-3">Package amount summary</h5>
                  {form.bookingId && amountSummary.total > 0 ? (
                    <>
                      {amountSummary.packageName ? (
                        <div className="mb-3">
                          <div className="text-secondary text-sm">Package</div>
                          <div className="fw-semibold">{amountSummary.packageName}</div>
                        </div>
                      ) : null}
                      <div className="rounded-3 bg-neutral-100 p-3 mb-3">
                        {amountSummary.lines.map((row) => (
                          <div key={`${row.label}-${row.rate}`} className="d-flex justify-content-between align-items-start gap-3 py-1">
                            <span>{row.label}</span>
                            <span className="fw-semibold text-nowrap">{formatCurrency(row.amount)}</span>
                          </div>
                        ))}
                        {amountSummary.pilgrims > 0 ? (
                          <div className="text-secondary text-sm mt-1">
                            {amountSummary.pilgrims} pilgrim{amountSummary.pilgrims === 1 ? "" : "s"} at {formatCurrency(amountSummary.total / amountSummary.pilgrims)} each
                          </div>
                        ) : null}
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span>Booking total</span>
                        <span className="fw-semibold">{formatCurrency(amountSummary.total)}</span>
                      </div>
                      <div className="d-flex justify-content-between py-1">
                        <span>Paid</span>
                        <span>{formatCurrency(amountSummary.paid)}</span>
                      </div>
                      {amountSummary.refunded > 0 ? (
                        <div className="d-flex justify-content-between py-1">
                          <span>Refunded</span>
                          <span>{formatCurrency(amountSummary.refunded)}</span>
                        </div>
                      ) : null}
                      <div className="d-flex justify-content-between py-1 border-top mt-2 pt-2">
                        <span className="fw-semibold">Outstanding</span>
                        <span className="fw-semibold">{formatCurrency(amountSummary.outstanding)}</span>
                      </div>
                    </>
                  ) : (
                    <div className="text-secondary">Select a booking to see the package amount split.</div>
                  )}
                </div>
              </div>
              <div className="card border-0 shadow-sm">
                <div className="card-body p-24">
                  <h5 className="mb-3">Recent payments</h5>
                  <Table>
                    <thead>
                      <tr>
                        <th>Booking</th>
                        <th className="text-end">Amount</th>
                        <th>Method</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {recentForBooking.length > 0 ? recentForBooking.map((row) => (
                        <tr key={row.transaction_id || row.payment_id}>
                          <td className="text-break">
                            {row.booking_id ? (
                              <Link to={`/bookings/${row.booking_id}`} className="agent-table-link">
                                {row.booking_reference || row.booking_id}
                              </Link>
                            ) : (
                              row.booking_reference || row.booking_id || "-"
                            )}
                          </td>
                          <td className="text-end">{formatCurrency(row.amount)}</td>
                          <td>{row.payment_method || "-"}</td>
                          <td><StatusBadge value={row.status || "pending"}>{formatPaymentStatus(row.status)}</StatusBadge></td>
                        </tr>
                      )) : (
                        <tr><td colSpan="4" className="text-secondary text-center py-4">{form.bookingId ? "No payments submitted for this booking yet." : "No payments submitted yet."}</td></tr>
                      )}
                    </tbody>
                  </Table>
                </div>
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </AgentLayout>
  );
}
